<script setup lang="ts">
import { ref, computed } from 'vue'
import { getAvailableLanguages, setLanguage } from '@/i18n'
import { useI18n } from 'vue-i18n'

interface Props {
  modelValue?: string | null
  onLanguageSelect?: (language: string) => void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const { locale } = useI18n()

const selectedLanguage = computed({
  get: () => props.modelValue || locale.value || 'de',
  set: (value: string) => {
    emit('update:modelValue', value)
    setLanguage(value)
    props.onLanguageSelect?.(value)
  },
})

const availableLanguages = getAvailableLanguages()
const isOpen = ref(false)

const getFlagEmoji = (languageCode: string): string => {
  const flagMap: Record<string, string> = {
    en: '🇬🇧',
    de: '🇩🇪',
  }
  return flagMap[languageCode] || '🌐'
}

const selectLanguage = (language: string) => {
  selectedLanguage.value = language
  isOpen.value = false
}
</script>

<template>
  <div class="relative">
    <button
      @click="isOpen = !isOpen"
      class="flex items-center gap-2 px-1 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-sm font-medium text-gray-700"
      :aria-label="`Select language: ${selectedLanguage}`"
      :title="`Current language: ${getFlagEmoji(selectedLanguage)}`"
    >
      <span class="text-xl">{{ getFlagEmoji(selectedLanguage) }}</span>
      <span class="hidden sm:inline">{{ selectedLanguage.toUpperCase() }}</span>
    </button>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen"
      class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
    >
      <button
        v-for="lang in availableLanguages"
        :key="lang.code"
        @click="selectLanguage(lang.code)"
        :class="[
          'w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors duration-200',
          selectedLanguage === lang.code ? 'bg-primary-50 border-l-4 border-l-primary-600' : 'border-l-4 border-l-transparent',
        ]"
      >
        <span class="text-lg">{{ getFlagEmoji(lang.code) }}</span>
        <span class="font-medium" :class="selectedLanguage === lang.code ? 'text-primary-600' : 'text-gray-700'">
          {{ lang.name }}
        </span>
        <span v-if="selectedLanguage === lang.code" class="ml-auto">
          <svg class="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped></style>
